import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { setLogoutHandler } from "../utils/authSession";
import { alleDatenCachesLeeren } from "../utils/loadCachedData";

const LOGIN_RETRY_PAUSE_MS = 1500;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef(null);
  // Verhindert, dass mehrere gleichzeitige/aufeinanderfolgende 401er oder der
  // Ablauf-Timer denselben "Session abgelaufen"-Toast mehrfach feuern.
  const expiredNotifiedRef = useRef(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // SECURITY NOTE: jwtDecode() does NOT validate the signature.
  // It only parses the JWT payload. We trust the role from the initial login response
  // from the backend. On subsequent requests, the backend will validate the token
  // signature and enforce authorization. If the backend rejects with 401, we log out.
  // This means a forged token with a fake "admin" role will fail on API calls.

  // === LOGOUT ===
  // reason "manual" = Nutzer klickt auf Abmelden, "expired" = Session/Token ungueltig.
  const logout = useCallback((reason = "manual") => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("username");
      localStorage.removeItem("last_login");
      // Gecachte Teams/Stationen/Ranking gehoeren zur Sitzung, nicht zum Geraet.
      alleDatenCachesLeeren();
      setUser(null);

      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }

      console.log("[Auth] User logged out", reason);
      if (reason === "expired") {
        toast.warn("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");
      } else {
        toast.info("Abgemeldet");
      }
    } catch (error) {
      console.error("[Auth] Logout error:", error);
      // Still clear state even if localStorage fails
      setUser(null);
    }
  }, []);

  // === SESSION ABGELAUFEN (einziger Einstiegspunkt) ===
  // Wird sowohl vom Auto-Logout-Timer als auch von fetchWithAuth (bei 401 /
  // fehlendem Token, ueber authSession.js) aufgerufen. Die Ref-Sperre sorgt
  // dafuer, dass egal wie viele parallele Requests gleichzeitig scheitern,
  // nur EIN Toast + EIN logout() ausgeloest wird - keine Toast-Schleife mehr.
  const handleSessionExpired = useCallback(() => {
    if (expiredNotifiedRef.current) return;
    expiredNotifiedRef.current = true;
    logout("expired");
  }, [logout]);

  useEffect(() => {
    setLogoutHandler(handleSessionExpired);
    return () => setLogoutHandler(null);
  }, [handleSessionExpired]);

  // === AUTO-LOGOUT PLANEN ===
  const scheduleAutoLogout = useCallback(
    (decodedToken) => {
      if (!decodedToken?.exp) return;

      const expMs = decodedToken.exp * 1000;
      const now = Date.now();
      const timeUntilExpiry = expMs - now;

      // 5-Sekunden-Puffer vor Ablauf (statt 60s für bessere UX)
      const logoutTime = Math.max(timeUntilExpiry - 5000, 0);

      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

      logoutTimerRef.current = setTimeout(() => {
        console.warn("[Auth] Token expired, logging out");
        handleSessionExpired();
      }, logoutTime);

      console.log(`[Auth] Auto-Logout geplant in ${Math.round(logoutTime / 1000)}s`);
    },
    [handleSessionExpired]
  );

  // === LOGIN ===
  const login = async (username, password) => {
    try {
      const loginAnfrage = () =>
        fetch(`${BACKEND_URL}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        });

      // Ein Netzwerkfehler (kein Netz, Server startet kalt) wird einmal nach kurzer
      // Pause wiederholt. Eine Antwort des Servers – auch eine Ablehnung – nie:
      // falsche Passwoerter duerfen die Sperre nach Fehlversuchen nicht doppelt zaehlen.
      const res = await loginAnfrage()
        .catch(async () => {
          console.warn("[Auth] Login: keine Verbindung, zweiter Versuch");
          await new Promise((resolve) => setTimeout(resolve, LOGIN_RETRY_PAUSE_MS));
          return loginAnfrage();
        })
        .catch(() => {
          // Netzwerkfehler meldet der Browser nur als englisches "Failed to fetch".
          throw new Error("Keine Verbindung zum Server. Bitte Netz prüfen und erneut versuchen.");
        });

      if (!res.ok) {
        // BEHOBENER FEHLER: gelesen wurde `message`; das Backend antwortet mit
        // `error` (englisch) bzw. bei der Sperre nach Fehlversuchen mit
        // `retry_after_seconds`. Die Nutzer sahen deshalb immer nur "Login
        // fehlgeschlagen" – auch dann, wenn sie 15 Minuten gesperrt waren.
        const errorData = await res.json().catch(() => ({}));
        let errorMsg;
        if (res.status === 429) {
          const minuten = Math.max(1, Math.ceil((errorData.retry_after_seconds || 900) / 60));
          errorMsg =
            `Zu viele Fehlversuche. Bitte in ${minuten} ${minuten === 1 ? "Minute" : "Minuten"} erneut versuchen.`;
        } else if (res.status === 400 || res.status === 401) {
          errorMsg = "Benutzername oder Passwort ist falsch.";
        } else if (res.status >= 500) {
          errorMsg = "Der Server hat einen Fehler gemeldet. Bitte später erneut versuchen.";
        } else {
          errorMsg = `Login fehlgeschlagen (HTTP ${res.status})`;
        }
        throw new Error(errorMsg);
      }

      const data = await res.json();

      // Validate response structure
      if (!data.token || !data.role) {
        console.error("[Auth] Invalid login response structure:", data);
        throw new Error("Ungültige Antwort vom Server");
      }

      // Validate token structure
      let decoded;
      try {
        decoded = jwtDecode(data.token);
        if (!decoded.exp) {
          throw new Error("Token has no expiry");
        }
      } catch (err) {
        console.error("[Auth] Invalid JWT:", err.message);
        throw new Error("Ungültiger Token vom Server");
      }

      // Reste einer frueheren Sitzung (ggf. mit anderer Rolle) verwerfen.
      alleDatenCachesLeeren();

      // Store auth data
      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.role);
      localStorage.setItem("username", data.username || username);
      if (data.last_login) {
        localStorage.setItem("last_login", data.last_login);
      }

      setUser({
        token: data.token,
        role: data.role,
        username: data.username || username,
      });

      // Neue Session: eine kuenftige Ablauf-Benachrichtigung soll wieder anzeigen.
      expiredNotifiedRef.current = false;
      scheduleAutoLogout(decoded);
      console.log("[Auth] Login successful");
      toast.success("Erfolgreich eingeloggt");
    } catch (error) {
      console.error("[Auth] Login error:", error.message);
      throw error;
    }
  };

  // === INITIALER TOKEN CHECK ===
  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    const username = localStorage.getItem("username");

    if (token && role) {
      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;

        if (!decoded.exp) {
          console.warn("[Auth] Token missing expiry claim");
          handleSessionExpired();
          return;
        }

        if (decoded.exp < now) {
          console.log("[Auth] Token expired on init");
          handleSessionExpired();
        } else {
          setUser({ token, role, username });
          scheduleAutoLogout(decoded);
          console.log("[Auth] User restored from localStorage");
        }
      } catch (err) {
        console.error("[Auth] Token validation error:", err.message);
        handleSessionExpired();
      }
    }

    setLoading(false);

    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [logout, scheduleAutoLogout, handleSessionExpired]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb eines AuthProviders verwendet werden");
  return ctx;
};
